import { useEffect, useState } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/* ─────────────────────────────────────────────────────────────────────────
   Shared master-data option loader for M17 rate forms (§35 dropdown sourcing).
   Pulls branding-type codes and measurement units from the config catalog and
   territory codes from the governance territory master.

   A read that FAILS leaves its list empty so the rest of the form still works,
   but it never pretends the list is genuinely empty: `loadError` says so, and
   the screens show it. A refused read in particular is not an empty master
   list — it is a reader who may not see it, and a picker that silently offers
   nothing tells him neither.
   ───────────────────────────────────────────────────────────────────────── */

interface MasterOptions {
  readonly brandingCodes: readonly string[];
  readonly brandingTypes: readonly BrandingTypeOption[];
  readonly territoryCodes: readonly string[];
  readonly uomCodes: readonly string[];
  readonly brandingItems: readonly BrandingItemOption[];
  /** Why a list is short, when it is. null when every read succeeded. */
  readonly loadError: string | null;
}

/** The code alone cannot be stored on a rate row; t200 keys on the id. */
export interface BrandingTypeOption {
  readonly brandingTypeId: string;
  readonly typeCode: string;
}

export interface BrandingItemOption {
  readonly brandingItemId: string;
  readonly label: string;
}

interface BrandingTypeRow {
  readonly brandingTypeId?: string;
  readonly code?: string; readonly typeCode?: string; readonly measurementUnit?: string;
}
interface TerritoryRow { readonly code?: string; readonly territoryCode?: string; readonly territoryId?: string }
interface BrandingItemRow {
  readonly brandingItemId?: string; readonly itemId?: string;
  readonly itemCode?: string; readonly itemName?: string;
}

export function useMasterOptions(token: string | null): MasterOptions {
  const { t } = useTranslation();
  const [opts, setOpts] = useState<MasterOptions>({ brandingCodes: [], brandingTypes: [], territoryCodes: [], uomCodes: [], brandingItems: [], loadError: null });

  useEffect(() => {
    let alive = true;
    let refused = false;
    const failures: string[] = [];
    // A refusal and a failure are different answers. Both leave the list empty
    // so the form still opens, and both are reported rather than hidden.
    const read = async <T,>(path: string): Promise<T[]> => {
      try {
        const r = await apiRequest<{ data: T[] }>(path, { token });
        return r.data ?? [];
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) refused = true;
        else failures.push((e as Error).message);
        return [];
      }
    };
    Promise.all([
      read<BrandingTypeRow>('/v1/m17/config/branding-types'),
      read<TerritoryRow>('/v1/m17/governance/territories'),
      read<BrandingItemRow>('/v1/m17/config/branding-items'),
    ]).then(([btRows, trRows, biRows]) => {
      if (!alive) return;
      const brandingCodes = Array.from(new Set(btRows.map((b) => b.code ?? b.typeCode).filter((v): v is string => !!v)));
      const brandingTypes = btRows
        .map((b) => ({ brandingTypeId: b.brandingTypeId ?? '', typeCode: b.code ?? b.typeCode ?? '' }))
        .filter((b) => b.brandingTypeId && b.typeCode);
      const uomCodes = Array.from(new Set(btRows.map((b) => b.measurementUnit).filter((v): v is string => !!v)));
      const territoryCodes = Array.from(new Set(trRows.map((tr) => tr.code ?? tr.territoryCode ?? tr.territoryId).filter((v): v is string => !!v)));
      const brandingItems = biRows
        .map((i) => ({
          brandingItemId: i.brandingItemId ?? i.itemId ?? '',
          label: i.itemName ? `${i.itemName}${i.itemCode ? ` (${i.itemCode})` : ''}` : (i.itemCode ?? ''),
        }))
        .filter((i) => i.brandingItemId && i.label);
      const messages = refused ? [t('m17.masterOptions.notPermitted'), ...failures] : failures;
      setOpts({
        brandingCodes, brandingTypes, territoryCodes, uomCodes, brandingItems,
        loadError: messages.length > 0 ? messages.join(' ') : null,
      });
    });
    return () => { alive = false; };
  }, [token, t]);

  return opts;
}
